/**
 * Per-row exception drawer - one unified, tabbed surface for every record:
 *
 *   1. Header  - eyebrow, H6 item title, a one-line subtitle (exception type +
 *                caller context) and the source-link row. Persistent above the
 *                tabs.
 *   2. Tabs    - Open issues (default) / Closed issues for every role; Notes /
 *                Activity Evo/PSC only. FP users get the two issue tabs alone -
 *                no action line or footer.
 *   3. Bodies  - Open issues: a thin object action line + the open-issues list
 *                (``IssuesEntryList``). Closed issues: a flat list of the
 *                recently resolved/ignored entries (no disclosure). Activity:
 *                the object timeline (``ActivityPanel``). Notes: the merged
 *                feed (``NotesPanel``).
 *   4. Footer  - on the Notes tab the composer (``NotesComposer``); on other
 *                tabs a slim "Add note" that jumps to Notes + focuses it.
 *
 * Mutations run through ``useDrawerObjectActions`` (object-level + per-row,
 * status-aware kebab) and the notes concern through ``useDrawerNotes`` (lazy -
 * fetches on first Notes-tab open), so this file stays focused on layout. Tab
 * shell mirrors ``HelpFeedbackDrawer``.
 *
 * Container: a custom <Paper> + <Slide> side panel - NOT an MUI Drawer.
 * Drawer + hideBackdrop leaves a11y in an awkward modal-ish-but-not state;
 * the explicit role="complementary" side panel sidesteps that. Mobile
 * (<600px) renders a Backdrop because the panel covers everything.
 */
import React from 'react'
import {
  Backdrop,
  Paper,
  Portal,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { APP_DRAWER_SHADOW } from '../../shared/AppDrawer'
import { poolwerxColors } from '../../../theme/colors'
import useCompareSalesDialog from '../../../hooks/useCompareSalesDialog'
import CompareSalesDialog from './CompareSalesDialog'
import CompareSalesUrlSync from './CompareSalesUrlSync'
import ReasonDialog from './dialogs/ReasonDialog'
import ExceptionDrawerIssuesBody from './exceptionDrawer/ExceptionDrawerIssuesBody'
import ExceptionDrawerPanel from './exceptionDrawer/ExceptionDrawerPanel'
import { useExceptionDrawerModel } from './exceptionDrawer/useExceptionDrawerModel'
import type { ExceptionDrawerProps } from './exceptionDrawer/props'
export type { ExceptionDrawerFranchiseData } from './exceptionDrawer/types'
export type { ExceptionDrawerProps } from './exceptionDrawer/props'

const ExceptionDrawer: React.FC<ExceptionDrawerProps> = ({
  open,
  onClose,
  row,
  loading: rowLoading = false,
  fpCode,
  getTitle,
  exceptionTypeName,
  allowIgnore = true,
  getRowTitle,
  getRowSubtitle,
  franchiseData,
  fpContext,
  reportHref,
  onCountDelta,
  onAfterMutation,
  initialNotesExpanded = false,
  focusCode,
  compareDeepLink = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // All state, effects and derived data live in the model hook; this file
  // stays the Portal/Slide container + wiring. The full model object feeds
  // the panel chrome; only the fields used directly here are destructured.
  const model = useExceptionDrawerModel({
    open,
    onClose,
    row,
    rowLoading,
    fpCode,
    getTitle,
    exceptionTypeName,
    getRowTitle,
    getRowSubtitle,
    franchiseData,
    onCountDelta,
    onAfterMutation,
    initialNotesExpanded,
    focusCode,
  })
  const {
    actionsGated,
    objectActions,
    openEntries,
    closedEntries,
    hasOpen,
    focusedCode,
    listOpenedFrom,
    emptyHealthy,
    rowSummary,
    rowSubtitleText,
    rowResetKey,
    detailsBlocks,
    compareSalesTarget,
  } = model

  // Header "Compare sales" action: present only when the backend stamped a
  // compare_sales target on the row (build_compare_sales_target), so the
  // inspector AND admin drawers share one eligibility contract.
  const compareSalesDialog = useCompareSalesDialog()
  const handleCompareSales =
    compareSalesTarget && fpCode
      ? () => compareSalesDialog.open({ fpCode, ...compareSalesTarget })
      : undefined

  // Issues tab body - the action line + open list (``'open'``) or the flat
  // closed list (``'closed'``) + healthy empty states. Deferred to a callback
  // so the panel renders only the active tab's list.
  const renderIssuesBody = (view: 'open' | 'closed') => (
    <ExceptionDrawerIssuesBody
      view={view}
      openEntries={openEntries}
      closedEntries={closedEntries}
      hasOpen={hasOpen}
      actionsGated={actionsGated}
      objectActions={objectActions}
      listOpenedFrom={listOpenedFrom}
      focusedCode={focusedCode}
      detailsBlocks={detailsBlocks}
      fpCode={fpCode}
      emptyHealthy={emptyHealthy}
      rowResetKey={rowResetKey}
    />
  )

  const panel = (
    <Paper
      role="complementary"
      aria-label="Exception details"
      elevation={0}
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        height: '100vh',
        // Desktop: scales 500 -> 580 with viewport. The ~580 cap is
        // intentionally wider than the shared 520 used by the other right-side
        // drawers (Audit / Activity / Feedback) - this one hosts four tabs.
        // Mobile stays full-width.
        width: isMobile ? '100vw' : 'clamp(500px, 34vw, 580px)',
        // Sit above any fullscreen table Dialog (also at zIndex.modal). Kebab
        // Menus inside this panel sit at zIndex.modal + 20 so they layer above.
        zIndex: theme.zIndex.modal + 10,
        borderRadius: 0,
        bgcolor: poolwerxColors.surface.primary,
        borderLeft: `1px solid ${theme.palette.divider}`,
        boxShadow: APP_DRAWER_SHADOW,
      }}
    >
      <ExceptionDrawerPanel
        model={model}
        renderIssuesBody={renderIssuesBody}
        onClose={onClose}
        fpContext={fpContext}
        reportHref={reportHref}
        fpCode={fpCode}
        onCompareSales={handleCompareSales}
      />
    </Paper>
  )

  return (
    <>
      {/* Portal to document.body so the position: fixed panel escapes any
       * ancestor that creates a containing block for fixed descendants. */}
      <Portal>
        {isMobile && (
          <Backdrop
            open={open}
            onClick={onClose}
            sx={{ zIndex: theme.zIndex.modal + 9 }}
          />
        )}
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          {panel}
        </Slide>
      </Portal>
      {/* The drawer's only reason dialog: object-level "Can't fix yet" (N tags)
        * and per-row Can't-fix-yet / Ignore (1 tag), driven by
        * useDrawerObjectActions. */}
      <ReasonDialog
        open={!!objectActions.reasonTarget}
        variant={objectActions.reasonTarget?.variant ?? 'block'}
        count={objectActions.reasonTarget?.tagIds.length ?? 1}
        exceptionTypeName={exceptionTypeName}
        itemTitle={rowSummary || undefined}
        itemSubtitle={rowSubtitleText}
        pending={objectActions.pending}
        onCancel={objectActions.closeReason}
        onSubmit={objectActions.submitReason}
      />
      {/* Header-launched LS<->Xero compare. modal+15 lifts it above the
        * drawer panel (modal+10) while staying below the kebab menus
        * (modal+20); table-launched compare dialogs keep the MUI default. */}
      <CompareSalesDialog
        {...compareSalesDialog.dialogProps}
        sx={{ zIndex: theme.zIndex.modal + 15 }}
      />
      {/* When this drawer owns the compare deep-link (exception route / admin
        * DQ), bridge the modal to the URL so it is shareable. */}
      {compareDeepLink && (
        <CompareSalesUrlSync
          fpCode={fpCode}
          target={compareSalesDialog.dialogProps.target}
          onOpen={compareSalesDialog.open}
        />
      )}
    </>
  )
}

export default ExceptionDrawer
